// ══════════════════════════════════════════════════════
//  react/VerdictCard.jsx — 投资建议卡片
//
//  两个变体：
//    <VerdictCard mode="full"  />  — 概览 tab 用，完整渲染
//    <VerdictCard mode="compact" /> — ShareCard 用，紧凑版
//
//  + getVerdictEvidence() — 为评分 tab 提供 evidence rows
// ══════════════════════════════════════════════════════

import { Lightbulb, AlertTriangle, MapPin } from 'lucide-react';
import { extract } from '../extractors/verdict';

const txt = (lang, zh, en) => (lang === 'en' ? en : zh);

const PHASE_LABEL = {
  no_token:               { zh: '未发币',     en: 'Pre-token',         color: null },
  post_tge_early:         { zh: '发币早期',   en: 'Post-TGE Early',    color: null },
  post_tge_stable:        { zh: '稳定期',     en: 'Stable Phase',      color: null },
  post_tge_high_unlock:   { zh: '高解锁期',   en: 'High Unlock',       color: '#dc2626' },
};

export function getVerdictEvidence(project, a, lang = 'zh') {
  const v = extract(project, a, lang);
  if (!v) return [];
  const rows = [];
  rows.push({
    label: txt(lang, '投资建议', 'Verdict'),
    val: lang === 'en' ? v.finalVerdictEn : v.finalVerdict,
    source: 'AI', positive: v.finalVerdict === '建议参与' ? true : v.finalVerdict === '暂不参与' ? false : null,
  });
  if (v.downgraded || v.upgraded) {
    rows.push({
      label: txt(lang, '基础评级', 'Base rating'),
      val: `${lang === 'en' ? v.baseVerdictEn : v.baseVerdict} → ${v.downgraded ? '↓' : '↑'}`,
      source: 'AI',
    });
  }
  if (v.triggeredRed.length > 0) {
    rows.push({ label: txt(lang, '风险因素', 'Risk factors'), val: `${v.triggeredRed.length}`, source: 'AI', positive: false });
  }
  if (v.triggeredGreen.length > 0) {
    rows.push({ label: txt(lang, '正面因素', 'Positive factors'), val: `${v.triggeredGreen.length}`, source: 'AI', positive: true });
  }
  return rows;
}

/** 完整 VerdictCard — 概览 tab */
export function VerdictCard({ project, a, lang = 'zh', S, C, mode = 'full' }) {
  const v = extract(project, a, lang);
  if (!v) return null;
  if (mode === 'compact') return <VerdictCompact v={v} lang={lang} />;
  return <VerdictFull v={v} lang={lang} S={S} C={C} />;
}

function VerdictFull({ v, lang, S, C }) {
  const phase = PHASE_LABEL[v.phase];
  return (
    <div style={{ ...(S?.card || { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }), borderLeft: `4px solid ${v.color}`, background: v.bg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: C?.muted || '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
            <Lightbulb size={11} strokeWidth={2} />{txt(lang, '投资建议（综合判断）', 'Investment Verdict')}
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: v.color, lineHeight: 1.2 }}>
            {lang === 'en' ? v.finalVerdictEn : v.finalVerdict}
          </div>
          {(v.downgraded || v.upgraded) && (
            <div style={{ fontSize: 10, color: C?.muted || '#6b7280', marginTop: 3 }}>
              {txt(lang, '基础评级：', 'Base rating:')}
              <span style={{ textDecoration: 'line-through', marginLeft: 4 }}>
                {lang === 'en' ? v.baseVerdictEn : v.baseVerdict}
              </span>
              <span style={{ marginLeft: 6, color: v.downgraded ? '#dc2626' : '#059669' }}>
                {v.downgraded ? txt(lang, '↓ 降级', '↓ downgraded') : txt(lang, '↑ 升级', '↑ upgraded')}
              </span>
            </div>
          )}
          {v.hardOverride && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#dc2626', marginTop: 3, fontWeight: 600 }}>
              <AlertTriangle size={11} strokeWidth={2} />{txt(lang, '硬性触发：', 'Hard override: ')}
              {v.hardOverride.startsWith('24h_crash')
                ? txt(lang, '24小时价格暴跌（疑似 Hack/暴雷）', '24h price crash (possible exploit)')
                : v.hardOverride.startsWith('7d_crash')
                ? txt(lang, '7日累计暴跌', '7d cumulative crash')
                : v.hardOverride}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', fontSize: 10, color: C?.muted || '#6b7280' }}>
          {phase && <div style={{ color: phase.color || undefined }}>{txt(lang, phase.zh, phase.en)}</div>}
          {v.newsSignalsState === 'absent' && (
            <div style={{ marginTop: 3, fontSize: 9, color: '#94a3b8' }}
              title={txt(lang,
                '本次评分未拿到 news_signals 字段（AI 评分返回缺失或上游 /api/ai/score 失败）',
                'news_signals field missing from this scoring run (AI omitted it or /api/ai/score errored)')}>
              {txt(lang, '新闻信号未读取', 'News signals unread')}
            </div>
          )}
          {v.newsSignalsState === 'empty' && (
            <div style={{ marginTop: 3, fontSize: 9, color: '#6b7280' }}
              title={txt(lang,
                'AI 已读取近期新闻，但未识别出红/绿旗信号 — 通常是近期无重要事件',
                'AI processed recent news and found no red/green signals — typically means no major events recently')}>
              {txt(lang, '· 近期无重要新闻', '· No notable news')}
            </div>
          )}
        </div>
      </div>

      {v.triggeredRed.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />
            {txt(lang, '风险信号（导致降级）', 'Risk Factors (caused downgrade)')}
          </div>
          {v.triggeredRed.map((f, i) => (
            <div key={i} style={{ fontSize: 11, color: '#7f1d1d', lineHeight: 1.6, paddingLeft: 8 }}>
              • {lang === 'en' ? f.en : f.zh}
            </div>
          ))}
        </div>
      )}

      {v.triggeredGreen.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: '#059669', marginBottom: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#059669' }} />
            {txt(lang, '正面信号', 'Positive Factors')}
          </div>
          {v.triggeredGreen.map((f, i) => (
            <div key={i} style={{ fontSize: 11, color: '#064e3b', lineHeight: 1.6, paddingLeft: 8 }}>
              • {lang === 'en' ? f.en : f.zh}
            </div>
          ))}
        </div>
      )}

      {v.triggeredRed.length === 0 && v.triggeredGreen.length === 0 && (
        <div style={{ fontSize: 11, color: C?.muted || '#6b7280', fontStyle: 'italic', padding: '4px 0' }}>
          {txt(lang, '当前数据未检测到强烈信号', 'No strong signals detected from current data.')}
        </div>
      )}

      {v.actionGuide.length > 0 && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.6)', borderRadius: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 4 }}>
            <MapPin size={11} strokeWidth={2} />{txt(lang, '操作要点', 'Action Guide')}
          </div>
          {v.actionGuide.map((g, i) => (
            <div key={i} style={{ fontSize: 11, color: '#1f2937', lineHeight: 1.6, paddingLeft: 8 }}>
              • {g}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** 紧凑版 — ShareCard 用，~80px 高度，纯文字 */
function VerdictCompact({ v, lang }) {
  const topRisk  = v.triggeredRed[0]?.[lang === 'en' ? 'en' : 'zh'];
  const topGreen = v.triggeredGreen[0]?.[lang === 'en' ? 'en' : 'zh'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: v.bg, borderLeft: `3px solid ${v.color}`, borderRadius: 6 }}>
      <div style={{ flex: '0 0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>
          <Lightbulb size={10} strokeWidth={2} />{txt(lang, '投资建议', 'Verdict')}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: v.color }}>
          {lang === 'en' ? v.finalVerdictEn : v.finalVerdict}
        </div>
      </div>
      <div style={{ flex: 1, fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
        {topRisk  && <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#7f1d1d' }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />{topRisk}</div>}
        {topGreen && <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#064e3b' }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', flexShrink: 0 }} />{topGreen}</div>}
        {!topRisk && !topGreen && (
          <div style={{ fontStyle: 'italic', color: '#94a3b8' }}>{txt(lang, '基础评级，无强信号', 'Base rating, no strong signals')}</div>
        )}
      </div>
    </div>
  );
}
